import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import {
  ScheduleRequestEventData,
  useCalendarEventsConfigurationShallowStore,
  useCalendarGridAccess,
} from '@frontend/schedule-helpers';
import { OperatoryAvailabilityBanner } from '@frontend/schedule-operatory-availability-banner';
import { InsuranceEligibilityCalendarCallout } from '@frontend/schedule-pulse-insurance-tab';
import { ContentLoader, useAlert } from '@frontend/design-system';
import { CalendarLocationSelectorModal } from '../../components/calendar-location-selector-modal';
import { ColumnEntityReorderActions } from '../../components/calendar-view/calendar-column-entity-list-section/column-entity-reorder-actions';
import { useColumnEntityReorderStore } from '../../components/calendar-view/providers';
import { CalendarViewOffices, OfficeCalendarView } from '../../components/calendar-view/types';
import { useGetCalendarColumnEntityData } from '../../hooks/scheduler-v3/use-get-calendar-column-entity-data';
import { useGetCalendarScheduleV3Data } from '../../hooks/scheduler-v3/use-get-calendar-schedule-v3-data';
import { useGetCalendarV3QueriesData } from '../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useCalendarStore } from '../../stores/calendar.store';
import { useScheduleActionsContextValue } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-actions-context-value';
import { useScheduleRequestModalContextValue } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-request-modal-context-value';
import { ScheduleRequestFormManager } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestList/ScheduleRequestFormManager';
import { ScheduleRequestPanel } from '../Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestModal/ScheduleRequestWrappers/ScheduleRequestPanel';
import { CalendarEventType } from '../Calendar/types';
import { isUUID } from '../Calendar/utils';
import { CalendarTimeLineViewV3, CalendarTimeLineViewV3Props } from './CalendarTimelineViewV3';
import { CalendarV3WithGrid } from './CalendarV3WithGrid';
import { CalendarHeader } from './components/header';

export const CalendarV3Container = () => {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { getScopeName, selectedLocationIds, getLocationTimeZone } = useSchedulingLocationInfo();
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);

  const {
    calendarEventsWithConvertedTimes,
    providersData,
    operatoryData,
    calendarLocationIds,
    calendarCounts,
    calendarTodayDate,
    isLoadingCalendarEvents,
    scheduledRequestsWithConvertedTimes,
    refetchScheduleRequestList,
  } = useGetCalendarV3QueriesData();
  const { providerSchedulesQuery } = useGetCalendarScheduleV3Data(
    calendarLocationIds.map((id) => ({
      locationId: id,
      providerIds: providersData?.providers.filter((p) => p.locationId === id).map((p) => p.id),
      operatoryIds: operatoryData?.operatories.filter((o) => o.locationId === id).map((o) => o.id),
    }))
  );

  const { calendarDate, calendarProviderIds, calendarOperatories } = useCalendarStore([
    'calendarDate',
    'calendarProviderIds',
    'calendarOperatories',
  ]);

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();

  const { isIntegratedOffice, isLoading: isLoadingIntegrations } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { isV3OperatorySupported, isLoading: isLoadingV3OperatorySupported } =
    SchedulerV3.Helpers.useCheckV3OperatorySupport();
  const { calendarEntityType, setCalendarEntityType } = useCalendarStore([
    'calendarEntityType',
    'setCalendarEntityType',
  ]);

  const { hasAiReceptionistAccess } = useAiScheduleAccess();
  const { isCalendarGridEnabled } = useCalendarGridAccess();

  const showOperatoryAvailabilityBanner =
    calendarEntityType === 'operatory' && isV3OperatorySupported && !hasAiReceptionistAccess;

  const {
    selectedScheduleRequest: selectedScheduleRequestEventData,
    setSelectedScheduleRequest: setSelectedScheduleRequestEventData,
    scheduleRequestModalContextValue,
  } = useScheduleRequestModalContextValue<ScheduleRequestEventData>({ refetchScheduleRequestList });
  const handleSetSelectedScheduleRequestEventData = useCallback(
    (request: ScheduleRequestEventData | null) => {
      setSelectedScheduleRequestEventData(request);
      scheduleRequestModalContextValue.scheduleRequestModalProps.openModal();
    },
    [setSelectedScheduleRequestEventData, scheduleRequestModalContextValue.scheduleRequestModalProps]
  );

  const selectedScheduleRequest = useMemo(
    () => ({ ...selectedScheduleRequestEventData, id: selectedScheduleRequestEventData?.requestId }),
    [selectedScheduleRequestEventData]
  );
  const scheduleActionsContextValue = useScheduleActionsContextValue();

  // Reset calendar entity type to 'provider' for offices which do not have operatory calendar view enabled
  useEffect(() => {
    if (isLoadingV3OperatorySupported) return;

    if (!isV3OperatorySupported && calendarEntityType === 'operatory') {
      setCalendarEntityType('provider');
    }
  }, [
    calendarEntityType,
    isIntegratedOffice,
    isLoadingIntegrations,
    isLoadingV3OperatorySupported,
    isV3OperatorySupported,
    setCalendarEntityType,
  ]);

  // Handle error while fetching provider schedules
  useEffect(() => {
    if (providerSchedulesQuery.isError) {
      alert.error(t('Error fetching provider schedules'));
    }
  }, [alert, providerSchedulesQuery.isError, t]);

  const sortedOperatories = useMemo(() => {
    const operatories = calendarOperatories.length
      ? operatoryData?.operatories?.filter(({ id }) => calendarOperatories.includes(id))
      : operatoryData?.operatories ?? [];
    return (
      operatories?.sort((a, b) =>
        ((a.displayName || a.externalName) ?? '').localeCompare((b.displayName || b.externalName) ?? '')
      ) ?? []
    );
  }, [calendarOperatories, operatoryData?.operatories]);

  const sortedProviders = useMemo(() => {
    const providers =
      (calendarProviderIds.length
        ? providersData?.providers.filter((p) => calendarProviderIds.includes(p.id))
        : providersData?.providers) ?? [];
    return providers
      .map((p) => ({ ...p, fullName: getFullName(p) }))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [calendarProviderIds, providersData?.providers]);

  const { getCalendarColumnEntityData } = useGetCalendarColumnEntityData({
    calendarEvents: calendarEventsWithConvertedTimes,
    scheduleRequests: scheduledRequestsWithConvertedTimes,
    providers: sortedProviders,
    operatories: sortedOperatories,
    providerSchedules: providerSchedulesQuery.data,
  });

  // TODO: Review this logic to handle the configured calendar event
  const handleConfiguredCalendarEvent = useCallback(
    (configuredCalendarEvent: CalendarEventType) => {
      const isProviderEvent = configuredCalendarEvent.providerId && isUUID(configuredCalendarEvent.providerId);
      const isOfficeEvent = configuredCalendarEvent.locationId && !configuredCalendarEvent.providerId;
      if (isOfficeEvent || isProviderEvent) {
        setConfiguredCalendarEvent(configuredCalendarEvent);
      }
    },
    [setConfiguredCalendarEvent]
  );

  const getOfficeCalendarViewObject = useCallback(
    (locationId: string): OfficeCalendarView => {
      return {
        officeName: getScopeName(locationId),
        officeTimezone: getLocationTimeZone(locationId),
        officeExceptions: [],
        hasAnyWorkingHoursSet: false,
        workingHours: [],
        availabilityExceptions: [],
        isAllDayOff: false,
        columnEntities: getCalendarColumnEntityData(locationId),
        locationId: locationId,
        calendarDateValue: formatDate(calendarDate, 'YYYY-MM-DD'),
      };
    },
    [getScopeName, getLocationTimeZone, getCalendarColumnEntityData, calendarDate]
  );

  const calendarObject = useMemo<CalendarViewOffices>(() => {
    if (calendarLocationIds.length)
      return calendarLocationIds.map((locationId) => getOfficeCalendarViewObject(locationId ?? ''));

    return [getOfficeCalendarViewObject(selectedLocationIds[0])];
  }, [calendarLocationIds, selectedLocationIds, getOfficeCalendarViewObject]);

  const calendarComponentProps = useMemo<CalendarTimeLineViewV3Props>(
    () => ({
      calendarObject: calendarObject,
      isLoadingCalendarEvents: isLoadingCalendarEvents,
      hasWSM: hasWSM,
      calendarDate: calendarDate,
      handleConfiguredCalendarEvent: handleConfiguredCalendarEvent,
      selectedScheduleRequestEventData: selectedScheduleRequestEventData,
      setSelectedScheduleRequestEventData: handleSetSelectedScheduleRequestEventData,
    }),
    [
      calendarObject,
      isLoadingCalendarEvents,
      hasWSM,
      calendarDate,
      handleConfiguredCalendarEvent,
      selectedScheduleRequestEventData,
      handleSetSelectedScheduleRequestEventData,
    ]
  );

  return (
    <>
      <section css={containerStyles} id='calendar-view-wrapper'>
        <InsuranceEligibilityCalendarCallout />
        {showOperatoryAvailabilityBanner && <OperatoryAvailabilityBanner />}
        {isInReorderMode ? (
          <ColumnEntityReorderActions />
        ) : (
          <CalendarHeader
            calendarEventsCount={calendarCounts.total}
            insuranceAlertCalendarEventsCount={calendarCounts.insurance}
            unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
            calendarTodayDate={calendarTodayDate}
          />
        )}
        <div css={{ position: 'relative', overflow: 'auto', flex: 1 }}>
          <ContentLoader
            show={isLoadingCalendarEvents}
            message={t('Loading appointments...')}
            css={{ justifyContent: 'flex-start', paddingTop: 'calc(50vh - 150px)' }}
          />
          {isCalendarGridEnabled ? (
            <CalendarV3WithGrid
              calendarEventsWithConvertedTimes={calendarEventsWithConvertedTimes}
              scheduledRequestsWithConvertedTimes={scheduledRequestsWithConvertedTimes}
              sortedProviders={sortedProviders}
              sortedOperatories={sortedOperatories}
              providerSchedules={providerSchedulesQuery.data}
            />
          ) : (
            <CalendarTimeLineViewV3 {...calendarComponentProps} />
          )}
        </div>
      </section>

      <CalendarLocationSelectorModal />
      {selectedScheduleRequestEventData && (
        <ScheduleRequestFormManager
          wrapper={ScheduleRequestPanel}
          scheduleRequestModalContextValue={scheduleRequestModalContextValue}
          selectedScheduleRequest={selectedScheduleRequest}
          scheduleActionsContextValue={scheduleActionsContextValue}
        />
      )}
    </>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  containerType: 'inline-size',
});
